// PublicTracking — vue publique anonyme accessible via lien partagé WhatsApp.
// Aucune authentification requise, aucune donnée sensible (email/téléphone client).
// Poll toutes les 15s pour la position live du chauffeur / livreur.

import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { MapPin, Navigation, Clock, Car, Package, Star, Loader2, ShieldCheck, CloudRain } from "lucide-react";
import WomaLogo from "@/components/WomaLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending: "En attente d'un chauffeur",
  assigned: "Chauffeur en route",
  picked_up: "Client à bord",
  in_transit: "En route vers destination",
  delivered: "Course terminée",
  cancelled: "Course annulée",
};

const STATUS_COLOR = {
  pending: "text-amber-400",
  assigned: "text-blue-400",
  picked_up: "text-emerald-400",
  in_transit: "text-emerald-400",
  delivered: "text-emerald-500",
  cancelled: "text-rose-500",
};

export default function PublicTracking() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/track/public/${token}`);
      setData(res.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("Ce lien n'est plus valide. Le client ne partage plus sa course.");
      } else {
        setError("Impossible de charger le suivi. Réessayez dans quelques secondes.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#020617] text-white flex flex-col items-center justify-center p-6">
        <WomaLogo className="w-20 h-20 mb-6" />
        <h1 className="text-xl font-black italic text-[#D4AF37] mb-3 uppercase">Suivi indisponible</h1>
        <p className="text-sm text-slate-400 text-center max-w-sm" data-testid="public-tracking-error">
          {error || "Lien introuvable."}
        </p>
      </div>
    );
  }

  const isTaxi = data.is_taxi;
  const status = data.status || "pending";
  const statusLabel = STATUS_LABEL[status] || status;
  const statusColor = STATUS_COLOR[status] || "text-slate-400";
  const fare = Number(data.fare || 0);
  const km = Number(data.distance_km || 0);
  const eta = data.eta_min;

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 pb-20" data-testid="public-tracking-page">
      {/* HEADER */}
      <header className="bg-gradient-to-r from-[#003594] via-[#003594] to-[#001A4D] px-5 py-5 border-b border-[#D4AF37]/20 shadow-lg">
        <div className="max-w-xl mx-auto flex items-center gap-3">
          <WomaLogo className="w-11 h-11" />
          <div>
            <h1 className="text-base font-black italic tracking-wide text-[#D4AF37] uppercase">
              Suivi en direct · WÔMA
            </h1>
            <p className="text-[9px] text-white/70 font-bold tracking-[0.3em] uppercase">
              {isTaxi ? "Course Taxi" : "Livraison"} · Partagé par le client
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto p-5 space-y-5">
        {/* Statut en gros */}
        <section
          className="bg-[#0B1E2B] border border-white/10 rounded-[2rem] p-6 text-center"
          data-testid="public-tracking-status"
        >
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-4">
            {isTaxi ? <Car className="w-7 h-7 text-[#D4AF37]" /> : <Package className="w-7 h-7 text-[#D4AF37]" />}
          </div>
          <p className={`text-xs font-black uppercase tracking-[0.3em] ${statusColor}`}>{statusLabel}</p>
          <h2 className="text-3xl font-black italic text-white tracking-tight mt-2">
            Course #{(data.order_id || "").substring(0, 8).toUpperCase()}
          </h2>
          {eta != null && status !== "delivered" && (
            <p className="mt-4 inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 px-4 py-2 rounded-full text-emerald-300 text-sm font-black">
              <Clock size={14} /> Arrivée estimée ~{eta} min
            </p>
          )}
          {data.rain_surge?.active && (
            <p className="mt-3 inline-flex items-center gap-2 bg-sky-500/10 border border-sky-500/30 px-3 py-1.5 rounded-full text-sky-300 text-[11px] font-bold">
              <CloudRain size={12} /> Mode Pluie actif · x{data.rain_surge.multiplier?.toFixed(2) || "1.30"}
            </p>
          )}
        </section>

        {/* Trajet */}
        <section className="bg-[#0B1E2B] border border-white/10 rounded-[2rem] p-5 space-y-3">
          <div className="flex items-start gap-3 bg-black/30 border border-blue-500/30 rounded-2xl p-3">
            <div className="w-9 h-9 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <MapPin size={14} className="text-blue-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-black text-blue-400 uppercase tracking-[0.3em]">Départ</p>
              <p className="text-sm font-black text-white break-words" data-testid="public-tracking-pickup">
                {data.pickup_address || "—"}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 bg-black/30 border border-emerald-500/30 rounded-2xl p-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Navigation size={14} className="text-emerald-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-black text-emerald-400 uppercase tracking-[0.3em]">Destination</p>
              <p className="text-sm font-black text-white break-words" data-testid="public-tracking-dropoff">
                {data.delivery_address || "—"}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between pt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">
            <span>Distance : <span className="text-white">{km > 0 ? `${km.toFixed(1)} km` : "—"}</span></span>
            {fare > 0 && (
              <span data-testid="public-tracking-fare">
                Tarif : <span className="text-[#D4AF37] text-sm">{fare.toLocaleString("fr-FR")} FCFA</span>
              </span>
            )}
          </div>
        </section>

        {/* Chauffeur */}
        {data.driver && (
          <section
            className="bg-[#0B1E2B] border border-[#D4AF37]/30 rounded-[2rem] p-5"
            data-testid="public-tracking-driver"
          >
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
              {isTaxi ? "Chauffeur" : "Livreur"} WÔMA
            </p>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center text-[#0F172A] font-black text-xl">
                {(data.driver.name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-lg font-black text-white truncate">{data.driver.name || "—"}</p>
                <div className="flex items-center gap-2 text-xs text-slate-300 font-bold mt-0.5">
                  <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                  <span className="text-white">{Number(data.driver.rating || 5).toFixed(1)}</span>
                  {data.driver.vehicle_type && (
                    <>
                      <span className="text-slate-500">·</span>
                      <span className="uppercase">{data.driver.vehicle_type}</span>
                    </>
                  )}
                </div>
                {data.driver.vehicle_plate && (
                  <span className="inline-block mt-2 bg-white text-slate-900 text-xs font-black tracking-widest px-3 py-1 rounded-md shadow-sm">
                    {data.driver.vehicle_plate}
                  </span>
                )}
              </div>
            </div>
          </section>
        )}

        <p className="text-center text-[10px] text-slate-500 font-bold uppercase tracking-widest flex items-center justify-center gap-2 pt-2">
          <ShieldCheck size={12} className="text-emerald-500" />
          Lien de suivi anonyme · Aucune donnée personnelle du client partagée
        </p>
      </main>
    </div>
  );
}
